// 内容仓库（v6.35）——查看/复制所有生成内容（今日热点/每日计划/多平台文章/口播稿/草稿/CSDN技术博客等）
// 读取 Vault：Content/Generated + Content/Drafts + Content/Daily + Content/HotTopics + Outputs/CSDN 目录
// 分类 Tab：全部 / 今日热点 / 每日计划 / 多平台文章 / 口播稿 / 草稿 / CSDN技术博客
// 平台筛选：全部 / 小红书 / 公众号 / 今日头条 / 知乎 / CSDN
// v6.25 改动：增加各目录加载状态显示 + 强化分类匹配 + 控制台日志便于诊断
// v6.26 改动：多平台文章按平台拆分预览——点平台筛选后，预览区只显示该平台的章节内容；
//             预览区顶部提供 全部/小红书/公众号/今日头条/知乎 Tab 切换；复制按钮复制当前平台内容
// v6.35 改动：新增 CSDN 文章目录（09_Knowledge/Outputs/CSDN）+ CSDN 分类 Tab；
//             平台匹配同时检查文件名与目录（目录名含 csdn 的文件视为 CSDN 平台）
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  FolderOpen,
  FileText,
  Copy,
  CheckCircle2,
  RefreshCw,
  LayoutGrid,
  Flame,
  CalendarDays,
  Layers,
  Mic,
  FileEdit,
  Smartphone,
  FileCode,
} from "lucide-react";

interface RepoFile {
  path: string;
  name: string;
  mtime: string;
  dir: string;
}

const DIRS = [
  "10_Operations（运营层）/Content/Generated",
  "10_Operations（运营层）/Content/Drafts",
  "10_Operations（运营层）/Content/Daily",
  "10_Operations（运营层）/Content/HotTopics",
  "09_Knowledge（知识中心）/Outputs/CSDN", // v6.35 新增：CSDN 技术博客文章
  "09_Knowledge（知识中心）/Outputs/知乎", // v6.38 新增：知乎专栏文章
  "09_Knowledge（知识中心）/Outputs/百家号", // v6.38 新增：百度百家号文章
  "09_Knowledge（知识中心）/Outputs/头条号", // v6.39 新增：今日头条文章
  "09_Knowledge（知识中心）/Outputs/小红书", // v6.41 新增：小红书图文
  "09_Knowledge（知识中心）/Outputs/抖音个人号", // v6.42 新增：抖音口播稿（GEO实操连载等）
  "09_Knowledge（知识中心）/Outputs/掘金", // v6.44 新增：掘金技术文章（企业AI架构/工程实战）
];

// v6.24 分类 Tab 定义（基于文件名/路径匹配）
type CategoryId =
  | "all" // 全部
  | "hot_topics" // 今日热点（HotTopics 目录 + 今日热点-*.md）
  | "daily_plan" // 每日计划（每日自媒体计划-*.md / 经营计划-*.md / 周度大纲）
  | "multi_platform" // 多平台文章（多平台文章-*.md）
  | "script" // 口播稿（口播稿-*.md）
  | "wechat_article" // 公众号文章（公众号文章-*.md，v6.32 新增）
  | "csdn" // CSDN 技术博客（Outputs/CSDN 目录，v6.35 新增）
  | "zhihu_article" // 知乎专栏（Outputs/知乎 目录，v6.38 新增）
  | "baijiahao_article" // 百度百家号（Outputs/百家号 目录，v6.38 新增）
  | "toutiao_article" // 今日头条（Outputs/头条号 目录，v6.39 新增）
  | "xiaohongshu_article" // 小红书图文（Outputs/小红书 目录，v6.41 新增）
  | "douyin_script" // 抖音口播稿（Outputs/抖音个人号 目录，v6.42 新增）
  | "juejin_article" // 掘金技术文章（Outputs/掘金 目录，v6.44 新增）
  | "draft"; // 草稿（Drafts 目录）

const CATEGORIES: { id: CategoryId; label: string; icon: typeof Flame }[] = [
  { id: "all", label: "全部", icon: LayoutGrid },
  { id: "hot_topics", label: "今日热点", icon: Flame },
  { id: "daily_plan", label: "每日计划", icon: CalendarDays },
  { id: "multi_platform", label: "多平台文章", icon: Layers },
  { id: "script", label: "口播稿", icon: Mic },
  { id: "wechat_article", label: "公众号文章", icon: FileText },
  { id: "csdn", label: "CSDN技术博客", icon: FileCode },
  { id: "zhihu_article", label: "知乎专栏", icon: FileText },
  { id: "baijiahao_article", label: "百家号文章", icon: FileText },
  { id: "toutiao_article", label: "头条号文章", icon: FileText },
  { id: "xiaohongshu_article", label: "小红书图文", icon: FileText },
  { id: "douyin_script", label: "抖音口播稿", icon: Mic },
  { id: "juejin_article", label: "掘金技术文", icon: FileCode },
  { id: "draft", label: "草稿", icon: FileEdit },
];

// v6.34 平台筛选（新增抖音个人号/抖音企业号）
// v6.7 新增 CSDN（技术博客）
// v6.38 新增百家号
type PlatformId = "all" | "xiaohongshu" | "wechat" | "toutiao" | "zhihu" | "douyin_personal" | "douyin_enterprise" | "csdn" | "baijiahao" | "juejin";

const PLATFORMS: { id: PlatformId; label: string }[] = [
  { id: "all", label: "全部平台" },
  { id: "douyin_personal", label: "抖音个人号" },
  { id: "douyin_enterprise", label: "抖音企业号" },
  { id: "xiaohongshu", label: "小红书" },
  { id: "wechat", label: "公众号" },
  { id: "toutiao", label: "今日头条" },
  { id: "zhihu", label: "知乎" },
  { id: "csdn", label: "CSDN" },
  { id: "baijiahao", label: "百家号" },
  { id: "juejin", label: "掘金" },
];

const PLATFORM_KEYWORDS: Record<Exclude<PlatformId, "all">, string[]> = {
  douyin_personal: ["抖音个人", "douyin_personal", "个人版", "个人号"],
  douyin_enterprise: ["抖音企业", "douyin_enterprise", "企业版", "企业号"],
  xiaohongshu: ["小红书", "xiaohongshu"],
  wechat: ["公众号", "wechat"],
  toutiao: ["头条", "toutiao", "今日头条"],
  zhihu: ["知乎", "zhihu"],
  csdn: ["csdn", "CSDN", "技术博客"],
  baijiahao: ["百家号", "baijiahao"],
  juejin: ["掘金", "juejin"],
};

/** 判断文件是否命中平台（文件名/目录/frontmatter 含平台关键词） */
function matchPlatform(name: string, dir: string, platform: PlatformId): boolean {
  if (platform === "all") return true;
  const kws = PLATFORM_KEYWORDS[platform];
  const lower = `${name} ${dir}`.toLowerCase();
  return kws.some((k) => lower.includes(k.toLowerCase()));
}

// ===== v6.26 多平台文章章节解析 =====
interface PlatformSection {
  id: PlatformId;
  label: string;
  content: string;
}

/** 各平台章节标题正则（匹配「## 一、小红书版」等） */
const PLATFORM_HEADER_RES: { id: PlatformId; label: string; re: RegExp }[] = [
  { id: "xiaohongshu", label: "小红书", re: /##\s*[一二三四五六七八九十]、\s*小红书版/ },
  { id: "wechat", label: "公众号", re: /##\s*[一二三四五六七八九十]、\s*公众号版/ },
  { id: "toutiao", label: "今日头条", re: /##\s*[一二三四五六七八九十]、\s*今日头条版/ },
  { id: "zhihu", label: "知乎", re: /##\s*[一二三四五六七八九十]、\s*知乎版/ },
];

/**
 * 从多平台文章正文中按平台拆分成章节。
 * 若文件不是多平台结构（无任何平台章节头），返回空数组。
 */
function extractPlatformSections(md: string): PlatformSection[] {
  const hits: { index: number; section: PlatformSection }[] = [];
  for (const p of PLATFORM_HEADER_RES) {
    const m = md.match(p.re);
    if (m && m.index !== undefined) {
      hits.push({ index: m.index, section: { id: p.id, label: p.label, content: "" } });
    }
  }
  if (hits.length === 0) return [];
  hits.sort((a, b) => a.index - b.index);
  return hits.map((h, i) => {
    const end = i + 1 < hits.length ? hits[i + 1].index : md.length;
    return { ...h.section, content: md.slice(h.index, end).trim() };
  });
}

/** 分类判断 */
function categorize(f: RepoFile): CategoryId {
  const name = f.name.toLowerCase();
  const dir = (f.dir || "").toLowerCase();
  // 今日热点：HotTopics 目录 或 文件名包含"今日热点"
  if (dir.includes("hottopics") || name.includes("今日热点")) return "hot_topics";
  if (dir.includes("drafts")) return "draft";
  // CSDN 技术博客：Outputs/CSDN 目录 或 文件名含 csdn/技术博客（v6.35 新增）
  if (dir.includes("csdn") || name.includes("csdn") || name.includes("技术博客")) return "csdn";
  // 百家号：Outputs/百家号 目录（v6.38）
  if (dir.includes("百家号") || name.includes("百家号")) return "baijiahao_article";
  // 头条号：Outputs/头条号 目录（v6.39）
  if (dir.includes("头条号") || name.includes("头条号")) return "toutiao_article";
  // 小红书：Outputs/小红书 目录（v6.41）
  if (dir.includes("小红书") || name.includes("小红书")) return "xiaohongshu_article";
  // 抖音口播稿：Outputs/抖音个人号 目录（v6.42）
  if (dir.includes("抖音") || name.includes("抖音")) return "douyin_script";
  // 掘金技术文章：Outputs/掘金 目录（v6.44 新增）
  if (dir.includes("掘金") || name.includes("掘金") || name.includes("juejin")) return "juejin_article";
  // 知乎：Outputs/知乎 目录（v6.40 新增，修复知乎文章无法归入"知乎专栏"分类）
  if (dir.includes("知乎") || name.includes("知乎")) return "zhihu_article";
  // 多平台文章：文件名包含"多平台文章" 或 "四平台"
  if (name.includes("多平台文章") || name.includes("四平台")) return "multi_platform";
  // 口播稿：文件名包含"口播稿" 或 "抖音双版本"
  if (name.includes("口播稿") || name.includes("抖音双版本")) return "script";
  // 公众号文章：文件名包含"公众号文章"（纯公众号单篇，v6.32 新增）
  if (name.includes("公众号文章")) return "wechat_article";
  // 每日计划：文件名包含"每日" + 计划相关 或 "周度大纲"
  if (name.includes("每日自媒体计划") || name.includes("每日计划") || name.includes("经营计划") || name.includes("周度大纲")) return "daily_plan";
  return "all";
}

const stripFm = (content: string) => {
  const m = content.match(/^---[\s\S]*?---\n*([\s\S]*)$/);
  return (m ? m[1] : content).trim();
};

/** 从 frontmatter title 或正文首个 # 标题解析文章真实标题（预览区用，避免显示文件名） */
function extractTitle(md: string): string {
  const fm = md.match(/^---\s*\n([\s\S]*?)\n---/);
  if (fm) {
    const t = fm[1].match(/^title:\s*(.+)$/m);
    if (t && t[1].trim()) return t[1].trim();
  }
  const h1 = md.match(/^#\s+(.+)$/m);
  if (h1) return h1[1].trim();
  return "";
}

/**
 * v6.43 递归列出目录下所有 .md 文件（含子目录）。
 * vault-api/list 不递归，遇到目录会以 isDirectory=true 返回而不展开子内容。
 * 抖音脚本存放在「Outputs/抖音个人号/抖音GEO连载/」两层，本函数下钻子目录，
 * 并统一归属为顶层目录 label（保证分类/平台判断正确）。
 */
async function listDirRecursive(dir: string, label: string): Promise<RepoFile[]> {
  const out: RepoFile[] = [];
  try {
    const resp = await fetch(
      `/vault-api/list?dir=${encodeURIComponent(dir)}&ext=.md`
    ).catch(() => null);
    if (!resp?.ok) return out;
    const r = await resp.json();
    if (!r?.success || !Array.isArray(r.files)) return out;
    for (const f of r.files as any[]) {
      if (f.name.startsWith(".")) continue;
      if (f.isDirectory) {
        // 递归下钻子目录，文件统一归属顶层 label
        const sub = await listDirRecursive(f.path, label);
        out.push(...sub);
      } else {
        out.push({ path: f.path, name: f.name, mtime: f.mtime || "", dir: label });
      }
    }
  } catch {
    // 单目录失败不影响其它目录
  }
  return out;
}

export default function ContentRepository() {
  const [files, setFiles] = useState<RepoFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<{ path: string; text: string } | null>(null);
  const [copied, setCopied] = useState(false);
  // v6.24 分类 + 平台筛选状态
  const [category, setCategory] = useState<CategoryId>("all");
  const [platform, setPlatform] = useState<PlatformId>("all");
  // v6.26 预览区当前查看的平台（跟随全局平台筛选，预览区可再切换）
  const [platformView, setPlatformView] = useState<PlatformId>("all");
  // v6.25 调试：每个目录加载的文件数
  const [dirStats, setDirStats] = useState<Record<string, number>>({});
  const [loadErrors, setLoadErrors] = useState<string[]>([]);
  // v6.41 品牌库：动态读取 08_Brand（品牌中心）/*/Content（Obsidian 品牌内容库）
  const [brand, setBrand] = useState<string>("all");
  const [brandGroups, setBrandGroups] = useState<{ brand: string; files: RepoFile[] }[]>([]);
  const [brandLoading, setBrandLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setDirStats({});
    setLoadErrors([]);
    const all: RepoFile[] = [];
    const errs: string[] = [];
    const stats: Record<string, number> = {};
    for (const dir of DIRS) {
      const dirName = dir.split("/").pop() || dir;
      // v6.43 使用递归读取：展开 subdir（如 抖音个人号/抖音GEO连载），避免跳过子目录文件
      const filesOfDir = await listDirRecursive(dir, dirName);
      all.push(...filesOfDir);
      stats[dirName] = filesOfDir.length;
    }
    all.sort((a, b) => (b.mtime || "").localeCompare(a.mtime || ""));
    setFiles(all);
    setDirStats(stats);
    setLoadErrors(errs);
    // v6.41 品牌库加载：08_Brand（品牌中心）/*/Content（Obsidian 品牌内容库）
    setBrandLoading(true);
    try {
      const bg: { brand: string; files: RepoFile[] }[] = [];
      const br = await fetch(
        `/vault-api/list?dir=${encodeURIComponent("08_Brand（品牌中心）")}&ext=`
      ).catch(() => null);
      if (br?.ok) {
        const brj = await br.json();
        const brandDirs = (brj.success && brj.files ? brj.files : []).filter(
          (f: any) => f.isDirectory
        ) as any[];
        for (const bd of brandDirs) {
          const brandName = bd.name;
          const contentDir = `08_Brand（品牌中心）/${brandName}/Content`;
          const resp = await fetch(`/vault-api/list-all?dir=${encodeURIComponent(contentDir)}&ext=.md`).catch(
            () => null
          );
          if (!resp?.ok) continue;
          const r = await resp.json();
          if (!r.success || !r.files) continue;
          const fs = (r.files as any[])
            .filter((f: any) => !f.name.startsWith("."))
            .map((f: any) => ({
              path: f.path,
              name: f.name,
              mtime: f.mtime || "",
              dir: `品牌·${brandName}`,
            }));
          if (fs.length > 0) bg.push({ brand: brandName, files: fs });
        }
      }
      setBrandGroups(bg);
    } catch {
      setBrandGroups([]);
    }
    setBrandLoading(false);
    setLoading(false);
    // v6.25 控制台日志：方便用户按 F12 查看诊断信息
    console.log("[内容仓库 v6.25] 加载完成:", {
      总数: all.length,
      各目录: stats,
      错误: errs,
      分类统计: all.reduce((acc: Record<string, number>, f) => {
        const c = categorize(f);
        acc[c] = (acc[c] || 0) + 1;
        return acc;
      }, {}),
    });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // v6.43 服务端一键补齐当日 News + 今日热点（WorkBuddy 兜底，桌面不跑时手动触发）
  const [sparkBusy, setSparkBusy] = useState(false);
  const [sparkMsg, setSparkMsg] = useState<string | null>(null);
  const sparkBackfill = useCallback(async () => {
    setSparkBusy(true);
    setSparkMsg(null);
    try {
      const resp = await fetch("/spark-backfill/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const j = await resp.json().catch(() => ({}));
      if (j && j.success) {
        setSparkMsg("已开始补齐当日 News + 今日热点，约 12 秒后自动刷新");
        window.setTimeout(() => void load(), 12000);
      } else {
        setSparkMsg((j && j.error) || "补齐失败，请稍后重试");
      }
    } catch {
      setSparkMsg("请求失败，请检查网络后重试");
    } finally {
      setSparkBusy(false);
    }
  }, [load]);

  // v6.24 按分类 + 平台过滤
  // v6.26 平台过滤：多平台文章文件（含四平台章节）对任何平台筛选都显示
  // v6.41 品牌库：选中品牌时只显示该品牌 Content 内容
  const brandView = useMemo(() => {
    if (brand === "all") return [];
    return brandGroups.find((g) => g.brand === brand)?.files ?? [];
  }, [brand, brandGroups]);

  const filteredFiles = useMemo(() => {
    if (brand !== "all") return brandView;
    return files.filter((f) => {
      if (category !== "all" && categorize(f) !== category) return false;
      if (platform !== "all") {
        const isMultiPlatform = categorize(f) === "multi_platform";
        if (!isMultiPlatform && !matchPlatform(f.name, f.dir, platform)) return false;
      }
      return true;
    });
  }, [files, category, platform, brand, brandView]);

  // v6.24 分类计数
  const categoryCount = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of CATEGORIES) counts[c.id] = 0;
    for (const f of files) counts[categorize(f)] = (counts[categorize(f)] || 0) + 1;
    counts.all = files.length;
    return counts;
  }, [files]);

  // v6.24 平台计数（基于当前分类）
  // v6.26 多平台文章文件对每个平台都计数（因为包含四平台章节）
  const platformCount = useMemo(() => {
    const base = category === "all" ? files : files.filter((f) => categorize(f) === category);
    const counts: Record<string, number> = { all: base.length };
    for (const p of PLATFORMS) if (p.id !== "all") counts[p.id] = 0;
    for (const f of base) {
      const isMultiPlatform = categorize(f) === "multi_platform";
      for (const p of PLATFORMS) {
        if (p.id !== "all" && (isMultiPlatform || matchPlatform(f.name, f.dir, p.id))) {
          counts[p.id]++;
        }
      }
    }
    return counts;
  }, [files, category]);

  const openFile = async (path: string) => {
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`).catch(
        () => null
      );
      if (resp?.ok) {
        const r = await resp.json();
        if (r.success) {
          setContent({ path, text: r.content });
          setCopied(false);
          // v6.26 打开文件时，预览区默认跟随当前全局平台筛选
          setPlatformView(platform);
        }
      }
    } catch {
      // 忽略
    }
  };

  // v6.26 解析当前文件的平台章节
  const sections = useMemo(() => {
    if (!content) return [];
    return extractPlatformSections(stripFm(content.text));
  }, [content]);

  // v6.26 当前预览文本：若选中具体平台且该平台章节存在 → 只显示该章节；否则全文
  const activeSection = useMemo(() => {
    if (!content || sections.length === 0 || platformView === "all") return null;
    return sections.find((s) => s.id === platformView) || null;
  }, [content, sections, platformView]);

  const previewText = activeSection
    ? activeSection.content
    : content
    ? stripFm(content.text)
    : "";

  const copyAll = async () => {
    if (!content) return;
    const text = activeSection ? activeSection.content : stripFm(content.text);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // 忽略
    }
  };

  return (
    <div className="space-y-3 p-4">
      {/* 头部 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <FolderOpen size={14} className="text-amber-400" />
          <span className="text-[13px] font-semibold text-white tracking-tight">内容仓库</span>
          <span className="text-[9px] font-mono text-gray-500">
            Generated / Drafts / Daily / HotTopics / CSDN / 品牌库
          </span>
          {/* v6.25 各目录加载状态 */}
          {!loading && Object.keys(dirStats).length > 0 && (
            <span className="text-[9px] font-mono text-emerald-500/80">
              {DIRS.map(d => {
                const dn = d.split("/").pop() || d;
                return `${dn}(${dirStats[dn] ?? 0})`;
              }).join(" · ")}
            </span>
          )}
          {loadErrors.length > 0 && (
            <span className="text-[9px] font-mono text-rose-400" title={loadErrors.join("\n")}>
              ⚠ {loadErrors.length} 个目录加载失败
            </span>
          )}
        </div>
        <button
          onClick={() => void load()}
          className="px-2.5 py-1 rounded-md text-[10px] font-mono text-gray-400 hover:bg-white/[0.04] border border-white/[0.08]"
        >
          刷新
        </button>
        <button
          onClick={() => void sparkBackfill()}
          disabled={sparkBusy}
          title="用服务端补当日缺失的 News 摘要 + 今日热点（WorkBuddy 未跑时的兜底按钮，已生成的日期会跳过）"
          className="px-2.5 py-1 rounded-md text-[10px] font-mono text-amber-300 hover:bg-amber-500/10 border border-amber-500/30 disabled:opacity-50"
        >
          {sparkBusy ? "补齐中…" : "服务端一键补齐"}
        </button>
      </div>
      {sparkMsg && (
        <div className="text-[10px] font-mono text-amber-200/90 bg-amber-500/10 border border-amber-500/20 rounded-md px-2.5 py-1">
          {sparkMsg}
        </div>
      )}

      {/* v6.24 分类 Tab */}
      <div className="flex items-center gap-1 flex-wrap">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = category === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                active
                  ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                  : "text-gray-500 hover:text-gray-300 border border-transparent hover:bg-white/[0.03]"
              }`}
            >
              <Icon size={11} />
              {c.label}
              <span className="text-[8px] font-mono text-gray-600">({categoryCount[c.id] || 0})</span>
            </button>
          );
        })}
      </div>

      {/* v6.24 平台筛选 */}
      <div className="flex items-center gap-1 flex-wrap">
        <Smartphone size={11} className="text-sky-400" />
        {PLATFORMS.map((p) => {
          const active = platform === p.id;
          return (
            <button
              key={p.id}
              onClick={() => {
                setPlatform(p.id);
                // v6.26 联动：切换平台筛选时，预览区也切到对应平台视图
                setPlatformView(p.id);
              }}
              className={`px-2 py-0.5 rounded text-[9px] font-mono transition-all ${
                active
                  ? "bg-sky-500/15 text-sky-300 border border-sky-500/30"
                  : "text-gray-600 border border-transparent hover:text-gray-300"
              }`}
            >
              {p.label} ({platformCount[p.id] || 0})
            </button>
          );
        })}
      </div>

      {/* v6.41 品牌库 Tab：Obsidian 08_Brand 品牌内容库 Content 目录 */}
      <div className="flex items-center gap-1 flex-wrap">
        <FolderOpen size={11} className="text-violet-400" />
        <span className="text-[9px] font-mono text-gray-500 mr-1">品牌库</span>
        <button
          onClick={() => setBrand("all")}
          className={`px-2 py-0.5 rounded text-[9px] font-mono transition-all ${
            brand === "all"
              ? "bg-violet-500/15 text-violet-300 border border-violet-500/30"
              : "text-gray-600 border border-transparent hover:text-gray-300"
          }`}
        >
          全部
        </button>
        {brandLoading ? (
          <span className="text-[9px] text-gray-600">加载品牌库…</span>
        ) : (
          brandGroups.map((g) => {
            const active = brand === g.brand;
            return (
              <button
                key={g.brand}
                onClick={() => setBrand(g.brand)}
                className={`px-2 py-0.5 rounded text-[9px] font-mono transition-all ${
                  active
                    ? "bg-violet-500/15 text-violet-300 border border-violet-500/30"
                    : "text-gray-600 border border-transparent hover:text-gray-300"
                }`}
              >
                {g.brand} ({g.files.length})
              </button>
            );
          })
        )}
        {!brandLoading && brandGroups.length === 0 && (
          <span className="text-[9px] text-gray-600">（暂无品牌归档，生成内容后将自动写入 08_Brand 品牌内容库）</span>
        )}
      </div>

      {loading ? (
        <div className="text-[11px] text-gray-500 py-6 text-center">加载中…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* 左列：文件列表 */}
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <FileText size={12} className="text-sky-400" />
              <span className="text-[11px] font-semibold text-sky-300">
                {brand !== "all" ? `${brand}品牌库` : CATEGORIES.find((c) => c.id === category)?.label + "内容"}
              </span>
              <span className="text-[9px] font-mono text-gray-600 ml-auto">{filteredFiles.length} 个文件</span>
            </div>
            {filteredFiles.length === 0 ? (
              <div className="text-[10px] text-gray-500 py-2">该分类/平台/品牌暂无内容</div>
            ) : (
              <div className="space-y-1.5 max-h-[520px] overflow-y-auto pr-1">
                {filteredFiles.map((f) => (
                  <button
                    key={f.path}
                    onClick={() => void openFile(f.path)}
                    className={`w-full text-left rounded-md border px-2 py-1.5 transition-colors ${
                      content?.path === f.path
                        ? "border-sky-500/40 bg-sky-500/10"
                        : "border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.05]"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-mono text-amber-500/80 shrink-0">{f.dir}</span>
                      <span className="text-[10px] text-gray-200 truncate">
                        {f.name.replace(".md", "")}
                      </span>
                    </div>
                    <div className="text-[9px] text-gray-600 mt-0.5 pl-4">
                      {f.mtime ? f.mtime.slice(0, 16).replace("T", " ") : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 右列：预览 + 复制 */}
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 min-h-[200px]">
            <div className="flex items-center gap-1.5 mb-2">
              <RefreshCw size={12} className="text-emerald-400" />
              <span className="text-[11px] font-semibold text-emerald-300">内容预览</span>
              {content && (
                <button
                  onClick={() => void copyAll()}
                  className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/10"
                >
                  {copied ? <CheckCircle2 size={10} /> : <Copy size={10} />}
                  {copied ? "已复制" : activeSection ? `复制${activeSection.label}版` : "复制全文"}
                </button>
              )}
            </div>
            {!content ? (
              <div className="text-[10px] text-gray-500 py-6 text-center">
                点左侧文件 → 这里预览 → 复制全文 → 粘贴到对应平台发布
              </div>
            ) : (
              <>
                <div className="text-[11px] font-semibold text-white mb-1 truncate" title={content.path}>
                  {extractTitle(content.text) || content.path.split("/").pop()?.replace(".md", "")}
                </div>
                <div className="text-[9px] font-mono text-gray-600 mb-1 truncate">
                  {content.path.split("/").pop()?.replace(".md", "")}
                </div>
                {/* v6.26 平台章节 Tab：多平台文章才显示 */}
                {sections.length > 0 && (
                  <div className="flex items-center gap-1 flex-wrap mb-1.5">
                    <button
                      onClick={() => setPlatformView("all")}
                      className={`px-2 py-0.5 rounded text-[9px] font-mono transition-all ${
                        platformView === "all"
                          ? "bg-sky-500/20 text-sky-200 border border-sky-500/40"
                          : "text-gray-500 border border-white/[0.06] hover:text-gray-300"
                      }`}
                    >
                      全部
                    </button>
                    {sections.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setPlatformView(s.id)}
                        className={`px-2 py-0.5 rounded text-[9px] font-mono transition-all ${
                          platformView === s.id
                            ? "bg-sky-500/20 text-sky-200 border border-sky-500/40"
                            : "text-gray-500 border border-white/[0.06] hover:text-gray-300"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
                {activeSection && (
                  <div className="text-[9px] font-mono text-emerald-400/80 mb-1">
                    ✓ 已按平台拆分：当前只显示 {activeSection.label} 版（共 {sections.length} 个平台章节）
                  </div>
                )}
                <div className="max-h-[480px] overflow-y-auto pr-1 border-t border-white/[0.05] pt-2">
                  <pre className="whitespace-pre-wrap text-[10px] leading-relaxed text-gray-300 font-sans">
                    {previewText}
                  </pre>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
